import { Button, Field, Input, MessageBar, MessageBarBody, makeStyles, tokens } from '@fluentui/react-components';
import { KEEP_OPEN, useDialogHandle, useDialogService, useT } from '@powerportalspro/react';
import { useState } from 'react';

// Same localization keys the Blazor `DialogServiceCustomDemo` resolves to, so both stacks pull from
// the same JSON.
const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Utilities.DialogService.DialogServiceCustomDemo';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    alignItems: 'flex-start',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

/** The dialog's content. It reaches its dialog through useDialogHandle rather than a prop. */
function RenameForm({ initialName }: { initialName: string }) {
  const styles = useStyles();
  const t = useT();
  const dialog = useDialogHandle<string>();
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);

  // What Save does: return the name, or keep the dialog open and say why.
  dialog.onPrimary(async () => {
    if (!name.trim()) {
      setError(t(`${STRINGS_BASE}.name-required`));
      return KEEP_OPEN;
    }
    return name.trim();
  });

  return (
    <div className={styles.form}>
      <Field label={t(`${STRINGS_BASE}.name-label`)}>
        <Input
          value={name}
          onChange={(_, d) => {
            setName(d.value);
            setError(null);
          }}
        />
      </Field>
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}

export function DialogServiceCustomSample() {
  const styles = useStyles();
  const t = useT();
  const dialogs = useDialogService();
  const [name, setName] = useState('Contoso');
  const [outcome, setOutcome] = useState<string | null>(null);

  const rename = async () => {
    // The content renders where dialogs are hosted, so what it needs from this page goes in as props.
    const result = await dialogs.showDialogAsync<string>(() => <RenameForm initialName={name} />, {
      title: t(`${STRINGS_BASE}.dialog-title`),
      primaryText: t(`${STRINGS_BASE}.save`),
      secondaryText: t(`${STRINGS_BASE}.cancel`),
    });

    // `canceled`, not the data's truthiness: a dialog can close with 0, false or ''.
    if (!result.canceled && result.data !== undefined) {
      setName(result.data);
      setOutcome(t(`${STRINGS_BASE}.result-saved`, [result.data]));
    } else {
      setOutcome(t(`${STRINGS_BASE}.result-canceled`));
    }
  };

  return (
    <div className={styles.root}>
      <Button appearance="primary" onClick={rename}>
        {t(`${STRINGS_BASE}.open-button`)}
      </Button>
      {outcome && <p>{outcome}</p>}
    </div>
  );
}
